import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Shared UI Library Components
import {
  CustomButton,
  DiscardModal,
  InputReadOnly,
  SpecialtyAdding,
  TDataRow
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './specialty-specialisations.scss';

// Store entities
import { fetchSpecializations } from '../../../../store/core/core.thunks';
import {
  updateCounsellorSpecialtySpecialisations
} from '../../../../store/current-counsellor/current-counsellor.thunks';
import { getCoreState } from '../../../../store/core/core.selectors';

// Models
import { ICounsellor } from '../../../../interfaces/counsellors.interfaces';
import { ISpeciality } from '../../../../interfaces/core.interfaces';

type TSpecialtySpecialisationsProps = {
  counsellor: ICounsellor;
};

export const SpecialtySpecialisations: FC<TSpecialtySpecialisationsProps> = (
  { counsellor }: TSpecialtySpecialisationsProps
) => {
  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);
  const [defaultValues, setDefaultValues] = useState<TDataRow[]>([]);

  const { speciality } = useSelector(getCoreState);

  const dispatch = useDispatch<any>();

  const initialData: { specialities: number[], specializations: string[] } = {
    specialities: counsellor.specialities.map(s => s.id),
    specializations: counsellor.specializations.map(s => `${ s.id }`)
  };

  let specialtyData: { specialities: number[], specializations: string[] } = {
    specialities: counsellor.specialities.map(s => s.id),
    specializations: counsellor.specializations.map(s => `${ s.id }`)
  };

  const compareHasChangesArrays = (oldArray: any[], newArray: any[]): boolean => {
    return oldArray.length !== newArray.length || oldArray.some((v, i) => !newArray.includes(v));
  }

  const getSpecialisationsNamesBySpecialty = (id: number): string[] => {
    return counsellor.specializations
      .filter(s => s.speciality_id === id)
      .map(s => s.name);
  }

  const generateGroupData = async (): Promise<void> => {
    const values = await Promise.all(counsellor.specialities.map(async (specialty) => {
      const { payload: options } = await dispatch(fetchSpecializations({
        speciality: [specialty],
        withoutDispatch: true
      }));
      return ({
        specialty,
        specialisation: {
          values: counsellor.specializations.filter(s => s?.speciality_id === specialty.id).map(s => `${ s.id }`),
          options: options,
          loading: false
        }
      })
    }));

    setDefaultValues(values);
  };

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      return;
    }
    toggleDiscardModal();
  }

  const changeSpecialtyInfoHandler = (data: {
    specialities: number[],
    specializations: string[]
  }): void => {
    setHasChanges(
      compareHasChangesArrays([...initialData.specialities], data.specialities) ||
      compareHasChangesArrays([...initialData.specializations], data.specializations)
    );

    specialtyData = data;
  }

  const fetchSpecializationsHandler = ({ speciality, withoutDispatch }: {
    speciality: ISpeciality[],
    withoutDispatch: boolean
  }): Promise<any> => {
    return dispatch(fetchSpecializations({ speciality, withoutDispatch }));
  }

  const saveChanges = async (): Promise<void> => {
    setSavingChanges(true);
    const { payload } = await dispatch(updateCounsellorSpecialtySpecialisations({
      specialities: specialtyData?.specialities ?? [],
      specializations: specialtyData?.specializations ?? [],
      worker_id: counsellor.id
    }));
    setSavingChanges(false);
    if (!payload) return;
    setEditMode(false);
    setHasChanges(false);
  }

  useEffect(() => {
    generateGroupData();
  }, [counsellor]);

  return (
    <div className="specialty-specialisations">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="specialty-specialisations--label personal-data--label">
        <div className="label-title">
          Specialty(ies) and Specialisation(s)
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ !!defaultValues?.length ? 'Edit' : 'Add' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <div className="specialty-specialisations--content personal-data--content">

        { !editMode && defaultValues.map((data, idx) => (
          <div key={ idx } className="content-item split-items">
            <div className="content-item--field">
              <InputReadOnly label={ 'Specialty' } textEllipsis={ true } chips={ [data?.specialty.name as string] }/>
            </div>

            <div className="content-item--field">
              <InputReadOnly label={ 'Specialisation(s)' } textEllipsis={ true }
                             chips={ getSpecialisationsNamesBySpecialty(data?.specialty.id) }/>
            </div>
          </div>
        )) }

        { editMode && <div className="content-item">
          <SpecialtyAdding specialityList={ speciality.list } fetchSpecializations={ fetchSpecializationsHandler }
                           defaultValue={ defaultValues } allowLastDelete={ true }
                           changeSpecialtyInfo={ changeSpecialtyInfoHandler }/>
        </div> }
      </div>
    </div>
  );
}
